<!-- eslint-disable no-console -->
<template>
  <div
    ref="plpContainer"
    class="flex flex-col py-5"
    :class="{ 'pr-4': isLandingPage }"
  >
    <div class="mt-4 flex gap-6">
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="transform -translate-x-full"
        enter-to-class="transform"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform"
        leave-to-class="transform -translate-x-full"
      >
        <div
          v-if="showFilter && windowWidth >= 700"
          class="w-[306px]"
          :class="isLandingPage ? 'hidden lg:flex' : ''"
        >
          <ZenniZnFilter
            v-model:selected-filters="selectedFilters"
            :filters="computedInitialFilters"
            :grouped="groupedFiltersFacetsName"
            :free-glasses="freeGlassesFacetsName"
            @hide-filters="showFilter = false"
          />
        </div>
      </Transition>
      <div
        v-if="showFilter"
        class="bg-grays-light hidden w-[1px] sm:block"
      ></div>
      <div class="flex flex-1 flex-col gap-6">
        <ZnBreadcrumbs :breadcrumbs="breadcrumbs" />
        <ZenniZnHeader
          v-if="selectedCategoryHeader"
          :background-image="selectedCategoryHeader.backgroundImage"
          :mobile-background-image="
            selectedCategoryHeader.mobileBackgroundImage
          "
          :title="selectedCategoryHeader.title"
          :subtitle="selectedCategoryHeader.subtitle"
          :text-color="selectedCategoryHeader.textColor"
        />
        <h1
          class="text-grays-darkest text-2xl font-semibold capitalize leading-[48px]"
          :class="selectedCategoryHeader ? 'mt-[63%] md:mt-0' : ''"
        >
          {{ title }}
        </h1>
        <div
          v-if="!hideFilters"
          class="self-start lg:self-end"
          :class="isLandingPage ? 'hidden' : ''"
        >
          <ZenniZnFindYourSizeModal>
            <template #trigger>
              <button
                class="items-center gap-2"
                :class="isLandingPage ? 'hidden lg:flex' : 'flex'"
              >
                <span
                  class="text-grays-darkest font-semibold underline underline-offset-2"
                  >Find your size</span
                >
                <ZenniIconsIconRuler size="sm" />
              </button>
            </template>
          </ZenniZnFindYourSizeModal>
        </div>
        <div
          v-if="!hideFilters"
          class="border-grays-light border-b"
          :class="{ 'hidden border-none lg:flex': isLandingPage }"
        ></div>
        <div
          class="flex-wrap items-center gap-4"
          :class="
            isLandingPage
              ? 'hidden'
              : 'border-grays-light flex border-b pb-4 md:border-none md:pb-0'
          "
        >
          <span class="text-grays-darkest order-1 flex-1 text-lg leading-[38px]"
            ><b>{{ totalItems }}</b> results</span
          >
          <div
            class="order-4 flex min-w-full justify-end md:order-2 md:min-w-0"
          >
            <ZenniZnColumnSelector v-model:columns="columns" />
          </div>
          <button
            v-if="!showFilter"
            class="text-grays-darkest order-2 flex items-center gap-[11px] rounded-lg border border-[#808080] bg-white px-2.5 py-[9px] font-semibold leading-5 md:order-3"
            @click="() => (showFilter = !showFilter)"
          >
            <span>Filters</span>
            <img
              alt="Show or Hide Filters"
              src="/img/icons/filter.svg"
              class="w-5"
            />
          </button>
          <ZnDropdown class="!z-0 order-3 md:order-4">
            <template #buttonText="{ open }">
              <div
                role="button"
                aria-label="Sort by"
                class="text-grays-darkest flex items-center gap-[11px] rounded-lg border border-[#808080] bg-white px-2.5 py-[9px] font-semibold leading-5"
              >
                <span>Sort by</span>
                <ZenniIconsIconChevronDown
                  size="sm"
                  class="transition-transform duration-300 ease-in-out"
                  :class="{ 'rotate-180': open }"
                />
              </div>
            </template>
            <template #content>
              <div class="flex min-w-[240px] flex-col bg-white">
                <ZenniZnSelectList
                  v-model="sortIndex"
                  :options="sortOptions"
                  @update:model-value="sortSearch"
                />
              </div>
            </template>
          </ZnDropdown>
        </div>
        <ZenniZnFilterChipSlider
          v-if="chipValues.length > 0 && !hideFilters"
          :chip-values="chipValues"
          @remove="
            (e) =>
              selectedFilters[e.category as keyof SelectedFilters]?.splice(
                selectedFilters[e.category as keyof SelectedFilters]?.indexOf(
                  e.value as any,
                ) ?? -1,
                1,
              )
          "
          @remove-all="clearFilters"
        />
        <div
          v-if="isGuestMode && !isCorpPerkStore && !isLandingPage"
          class="bg-teal-lightest flex w-full cursor-pointer"
          @click="signinStore.setShowSignIn(true)"
        >
          <div class="bg-teal-primary w-1 rounded-l-full rounded-r-lg"></div>
          <div
            class="flex flex-1 flex-col items-center justify-between gap-2 py-3 pl-7 pr-9 font-semibold md:flex-row"
          >
            <span class="flex-1"
              >Sign in to see what glasses you qualify for and what your
              benefits cover.</span
            >
            <span class="text-teal-primary self-end underline md:self-auto"
              >Check my benefits</span
            >
          </div>
        </div>
        <div
          v-if="totalItems === 0 && !loadingResult && !loading"
          class="text-grays-darkest flex flex-1 items-center justify-center"
        >
          <div class="flex flex-col gap-4">
            <h2 class="text-center text-2xl font-semibold xl:text-left">
              No results were found for
              {{ query ? `"${query}"` : 'your search' }}
            </h2>
            <h4 class="text-xl font-semibold">Search tips</h4>
            <ul class="list-disc pl-6">
              <li>Double check your spelling</li>
              <li>Try searching by product type or model number</li>
            </ul>
          </div>
        </div>
        <div
          v-if="computedHits.length > 0"
          class="place-items-start"
          :class="[
            columns === '1'
              ? 'grid-cols-1'
              : columns === '2'
                ? 'grid-cols-2'
                : 'grid-cols-2 lg:grid-cols-3',
            isLandingPage
              ? 'max-w-scrollable-section flex w-full flex-1 gap-x-12 gap-y-6 overflow-scroll pb-4 pr-4 lg:grid lg:max-w-7xl lg:gap-x-12 lg:overflow-auto lg:pb-0 lg:pr-0'
              : 'grid gap-x-12 gap-y-6',
          ]"
        >
          <ZenniZnProductCard
            v-for="(product, i) in computedHits"
            :tabindex="0"
            :id="product.id"
            :key="product.id"
            class="mx-auto w-full"
            v-bind="product"
            :vto-skus="vtoAvailableFrameSkus"
            :loading="loadingResult || loading"
            :available-credit="availableCredit"
            :badges="product.badges"
            @go-to-product="goToProduct"
            @try-on-clicked="handleTryOnClick"
            :data-test-id="String(i)"
          />
        </div>
        <ZenniZnPagination
          v-if="totalItems > 0 && !loadingResult && !loading && !hideFilters"
          :class="isLandingPage ? 'hidden lg:flex' : ''"
          :total-items="totalItems"
          :current-page="page"
          :page-size="pageSize"
          class="mt-4"
          @update:current-page="changePage"
        />
      </div>
    </div>

    <ZenniZnFilterModal
      v-model:selected-filters="selectedFilters"
      :is-open="windowWidth < 700 && showFilter"
      :filters="computedInitialFilters"
      :grouped="groupedFiltersFacetsName"
      :free-glasses="freeGlassesFacetsName"
      @close="showFilter = false"
    />

    <ClientOnly>
      <FittingboxModal />
    </ClientOnly>
  </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useWindowSize } from '@vueuse/core'
import { useFittingbox } from '~/composables/useFittingbox'
import { useFilters } from '~/composables/useFilters'
import { useProducts } from '~/composables/useProducts'
import { useEmployeeAlgolia } from '~/composables/useEmployeeAlgolia'
import {
  ref,
  useGetAlgoliaSearchIndex,
  useRouter,
  useRoute,
  computed,
  watch,
  onMounted,
  onBeforeMount,
  useNuxtApp,
} from '#imports'
import {
  KIDS_GENDER_GROUP_STRING,
  MEN_GENDER_GROUP_STRING,
  SPORTS_GROUP_STRING,
  WOMEN_GENDER_GROUP_STRING,
} from '~/data/categories'
import {
  WRAP_AROUND_GROUP,
  CAT_EYE_GROUP,
  XS_SIZE_GROUP,
  XL_SIZE_GROUP,
  MED_SIZE_GROUP,
  FULL_RIM_GROUP,
  HALF_RIM_GROUP,
} from '~/data/filter-groups'
import { useGuestStore } from '~/store/guest'
import { useSignInStore } from '~/store/signin'
import { BUDGET_FILTERS } from '~/data/budget-filters'
import {
  type SelectedFilters,
  type ZnFilterProps,
  type Filters,
  type ZnFilterChipValue,
  FILTERS_ARRAY,
} from './filter-types'
import type { ZnHeaderProps } from '~/components/Zenni/ZnHeader/types'
import type { ZnAlgoliaProductVariant } from '@/types/algolia-results/algolia-types'
import type { ZnPlpProps, ZnPlpEmits } from './types'
import { useEmployeeGtmTrackEvent } from '~/composables/useEmployeeGtmTrackEvent'
import { CATEGORIES_METADATA } from '~/data/categories'

const { plpTrackEvent, plpClickItemTrackEvent } = useEmployeeGtmTrackEvent()
const { width: windowWidth } = useWindowSize()
const plpContainer = ref()

const props = withDefaults(defineProps<ZnPlpProps>(), {
  isLandingPage: false,
  limit: 20,
})
const emit = defineEmits<ZnPlpEmits>()

const showFilter = ref(false)
const columns = ref('2')

const { getAvailableFrames } = useFittingbox()

const algoliaIndex = useGetAlgoliaSearchIndex()

const {
  mapFilters,
  processMapFilters,
  mapHitToProductCard,
  groupedFiltersFacetsName,
  freeGlassesFacetsName,
} = useFilters()

const selectedCategory = computed<string | undefined>(() => {
  return route.params?.slug?.[0]
})

const selectedCategoryHeader = computed<ZnHeaderProps | undefined>(() => {
  return selectedCategory.value
    ? CATEGORIES_METADATA(!!currentGift.value?.discountPercentage).find(
        (card) =>
          selectedCategory.value && card.href.includes(selectedCategory.value),
      )?.header
    : undefined
})

const scrollToTop = () => {
  if (process.client && !props.isLandingPage) {
    plpContainer.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    })
  }
}

const router = useRouter()

const initialPage =
  router.currentRoute.value?.query?.page &&
  +router.currentRoute.value?.query?.page > 1
    ? +router.currentRoute.value?.query?.page
    : 1

const {
  getProducts,
  facets,
  totalItems,
  page,
  pageSize,
  hits,
  setPage,
  loading,
  mapFacets,
} = useProducts<ZnAlgoliaProductVariant>({
  hitsPerPage: props.limit ?? 12,
  page: initialPage - 1,
  indexName: algoliaIndex,
})

watch(totalItems, (newTotalItems) => {
  emit('update:totalItems', newTotalItems)
})

const query = ref('')
const loadingResult = ref(true)
const allowedFilters = ['Inventory_Level_search']

const route = useRoute()

const sortOptions = [
  {
    slug: 'price_asc',
    label: 'Price: Low to high',
    value: `${algoliaIndex}_price_asc`,
  },
  {
    slug: 'price_desc',
    label: 'Price: High to low',
    value: `${algoliaIndex}_price_desc`,
  },
]

const labelMap: Record<string, string> = {
  nonprescription: 'Non-Prescription',
  prescription: 'Prescription',
  progressive: 'Progressives',
  [KIDS_GENDER_GROUP_STRING]: 'Kids',
  [MEN_GENDER_GROUP_STRING]: 'Men',
  [WOMEN_GENDER_GROUP_STRING]: 'Women',
  [SPORTS_GROUP_STRING]: 'Sports',
  [WRAP_AROUND_GROUP.filter]: 'Wrap-Around',
  [CAT_EYE_GROUP.filter]: 'Cat-Eye',
  [XS_SIZE_GROUP.filter]: 'XS',
  [XL_SIZE_GROUP.filter]: 'XL',
  [MED_SIZE_GROUP.filter]: 'M',
  [FULL_RIM_GROUP.filter]: 'Full-Rim',
  [HALF_RIM_GROUP.filter]: 'Half-Rim',
}

const selectedFilters = ref<SelectedFilters>(props.initialFilter ?? {})
const initialFilters = ref<ZnFilterProps['filters']>()
const sortIndex = ref('')

const computedInitialFilters = computed<ZnFilterProps['filters']>(() => {
  if (!initialFilters.value) {
    return {}
  }
  const filters = JSON.parse(JSON.stringify(initialFilters.value))
  return processMapFilters(filters, filtersToHide.value)
})

const chipValues = computed<ZnFilterChipValue[]>(() => {
  const arr: ZnFilterChipValue[] = []

  const selectedFiltersKeys = Object.keys(selectedFilters.value)

  selectedFiltersKeys.forEach((key) => {
    selectedFilters.value[key as Filters]?.forEach((value) => {
      arr.push({
        label:
          labelMap[value] ||
          computedInitialFilters.value![
            key as keyof SelectedFilters
          ]?.facet?.values.find(
            (e: any) => e.value.toLowerCase() === value.toLocaleLowerCase(),
          )?.label ||
          (key.toLowerCase().includes('price') ? `$${value}` : ''),
        value,
        category: key,
      })
    })
  })
  if (props.bestSelling) {
    arr.push({
      label: 'Best Seller',
      value: 'bestselling',
      category: 'bestselling',
      permanent: true,
    })
  }
  return arr
})

const clearFilters = () => {
  const keys = Object.keys(selectedFilters.value)
  for (const key of keys) {
    selectedFilters.value[key as Filters] = undefined
  }
}

const computedHits = computed<any[]>(() => {
  // the next line is to simulate product while loading and show skeletons
  if (loadingResult.value || loading.value) {
    return Array.from({ length: pageSize.value }, (_, i) => ({ i }))
  }
  const frames = hits.value.map((hit: ZnAlgoliaProductVariant) =>
    mapHitToProductCard(
      hit,
      router.currentRoute.value?.query?.activeImage === 'tinted',
    ),
  )

  // Sorting to put out of stock frames to the end
  return frames.sort((a, b) => {
    if (!a.variants?.[0]?.sku_in_stock && b.variants?.[0]?.sku_in_stock) {
      return 1
    }
    if (!b.variants?.[0]?.sku_in_stock && a.variants?.[0]?.sku_in_stock) {
      return -1
    }
    return 0
  })
})

const changePage = (page: number) => {
  setPage(page - 1, sortIndex.value)
  scrollToTop()
}

const updateRouteParams = ({ force = false }: { force?: boolean } = {}) => {
  if (props.isLandingPage && !force) {
    return
  }
  const params: SelectedFilters & {
    sort?: string
    query?: string
    page?: string
  } = {
    ...selectedFilters.value,
  }
  if (sortIndex.value) {
    params.sort = getSearchSlug(sortIndex.value)
  }
  if (query.value) {
    params.query = query.value
  }
  if (page.value > 1) {
    params.page = page.value.toString()
  } else {
    params.page = undefined
  }
  router.push({
    query: {
      ...(router.currentRoute.value?.query || {}),
      ...params,
    },
  })
}

const normalizeFilters = (filters: Record<string, string[]>) => {
  if ((filters.Frame___Prescription_types_search?.length ?? 0) > 0)
    if (filters.Frame___Prescription_types_search.length === 2) {
      return {
        ...filters,
        Frame___Prescription_types_search: [],
      }
    } else if (
      filters.Frame___Prescription_types_search[0] === 'prescription'
    ) {
      return {
        ...filters,
        Frame___Prescription_types_search: [
          'bifocal',
          'progressive',
          'singlevision',
          'reader',
        ],
      }
    }
  return filters
}

const search = async (
  querySearch?: string,
  selectedFiltersSearch?: Record<string, string[]>,
  sortBy?: string,
) => {
  let filters = {
    ...(selectedFiltersSearch || selectedFilters.value),
    categories: ['frame'],
    objectType: ['product-variant'],
    // enabled: [true],
  }
  if (props.productCategories) {
    filters = {
      ...filters,
      categories: props.productCategories,
    }
  }
  const normalizedFilters = normalizeFilters(filters)
  await getProducts({
    query: querySearch || query.value,
    filters: normalizedFilters,
    sortBy: sortBy || sortIndex.value,
    bestSelling: props.bestSelling ?? undefined,
  })
  if (!initialFilters.value) {
    initialFilters.value = { ...facets.value } as any
  }
  // Next function updates the initial filter facets with the current product count
  // There was a task to NOT DO this ald keeps all initial counts.
  // if this changes, uncomment the line below
  // updateInitialFilterFacets();
}

const getSearchSlug = (sort: string) => {
  return sortOptions.find((option) => option.value === sort)?.slug
}

const sortSearch = async (sort: string) => {
  sortIndex.value = sort
  scrollToTop()
  updateRouteParams({ force: true })
}

const updateInitialFilterFacets = () => {
  if (!facets.value) return
  Object.keys(facets.value).forEach((key) => {
    const mapFilterItem = (mapFilters as any)[key as Filters]
    const initialFilterItem = initialFilters.value![key as Filters]
    const facetItem = facets.value![key as Filters]
    if (mapFilterItem && initialFilterItem?.values && facetItem?.values) {
      initialFilterItem.values = initialFilterItem.values.map((val: any) => ({
        ...val,
        productCount:
          facetItem.values.find(
            (e: any) => e.value.toLowerCase() === val.value.toLowerCase(),
          )?.productCount || 0,
      })) as any
    }
  })
}

watch(
  selectedFilters,
  async () => {
    const queryParams = FILTERS_ARRAY.reduce((acc, filter) => {
      acc[filter] = selectedFilters.value[filter as Filters] ?? undefined
      return acc
    }, {} as any)

    router.replace({
      query: {
        ...router.currentRoute.value.query,
        ...queryParams,
      },
    })
  },
  { deep: true },
)

watch(
  () => page.value,
  () => updateRouteParams({}),
  { deep: true },
)

const goToProduct = ({ sku, objectId }: { sku: string; objectId?: string }) => {
  // store last category for back button
  const product = hits.value.find((e) => e.objectID === objectId)
  plpClickItemTrackEvent(
    {
      sku,
      name: product?.products[sku]?.Name,
      rgb_color_code: product?.products[sku]?.Main_Color,
      price: product?.products[sku]?.Price,
    },
    0,
    { guest: isGuestMode.value },
  )
  router.push({
    path: `/products/${sku}`,
    query: {
      gift: route.query.gift,
      objectId,
      activeImage: route.query?.activeImage,
    },
  })
}

const reloadQueryParamsAndSearch = async () => {
  const params = JSON.parse(JSON.stringify(route.query))
  const paramsKeys = Object.keys(params)
  if (paramsKeys.length > 0) {
    paramsKeys.forEach((key) => {
      if (key === 'sort') {
        sortIndex.value =
          sortOptions.find((option) => option.slug === (params[key] as string))
            ?.value ?? ''
      }
      if (
        Object.keys(mapFilters).includes(key) ||
        allowedFilters.includes(key)
      ) {
        selectedFilters.value[key as Filters] =
          typeof params[key] === 'string'
            ? [params[key] as string]
            : (params[key] as string[])
      }
      if (key === 'query') {
        query.value = params[key] as string
      }
    })
  }
  await search()
}

watch(
  () => route.query,
  async () => {
    await reloadQueryParamsAndSearch()
  },
  { deep: true },
)

const guestStore = useGuestStore()
const signinStore = useSignInStore()
const { currentGift, isGuestMode, isCorpPerkStore, isZunniesStore } =
  storeToRefs(guestStore)
const filtersToHide = computed(() => {
  const lensFilter = currentGift.value?.lensFilter || ''
  if (lensFilter.includes('NOT') && lensFilter.includes('nonprescription')) {
    return [
      {
        facetName: 'Frame___Prescription_types_search',
        value: 'nonprescription',
      },
    ]
  }
  if (isZunniesStore.value) {
    return [
      {
        facetName: 'Frame___Prescription_types_search',
        value: 'progressive',
      },
    ]
  }
  return []
})

onBeforeMount(() => {
  const creditLeft = currentGift.value
    ? guestStore.calculateGiftAmt(currentGift.value)
    : 0
  if (!props.isLandingPage && creditLeft >= 25) {
    const selectedFilter = BUDGET_FILTERS.sort(
      (a, b) => b.price - a.price,
    ).find((filter) => filter.price <= creditLeft)
    selectedFilters.value = {
      ...(selectedFilters.value as any),
      ...(selectedFilter?.initialFilter as any),
    }
  }
})

onMounted(async () => {
  // windowWidth is only available on client side
  showFilter.value = windowWidth.value >= 700 && !props.isLandingPage
  await reloadQueryParamsAndSearch()
  loadingResult.value = false

  const products = hits.value.map((e) => Object.values(e.products)).flat()
  plpTrackEvent(
    products.map((p) => ({
      sku: p.sku,
      name: p.Name,
      rgb_color_code: p.Main_Color,
      price: p.Price,
    })),
    { guest: isGuestMode.value },
  )

  if (props.isLandingPage) {
    columns.value = '3'
  }
})

const vtoAvailableFrameSkus = ref<string[]>([])

watch(
  () => hits.value,
  () => {
    getAvailableFrames(
      hits.value.flatMap((variant) =>
        Object.values(variant.products).map((p) => p.sku),
      ),
    ).then((res: string[]) => (vtoAvailableFrameSkus.value = res))
  },
  { deep: true },
)

const { $fittingboxContext } = useNuxtApp()
const handleTryOnClick = (sku: string) => {
  if (sku) {
    $fittingboxContext.selectFrame(sku)
  }
}

// SSR
// this search is for getting all initial available facets and its product count

const { search: initialSearch, result: initialResults } = useEmployeeAlgolia()
let filters = 'objectType:"product-variant"'
props.productCategories?.forEach((category) => {
  filters += ` AND categories:${category}`
})
await initialSearch({
  queryParams: {
    query: '',
    requestOptions: {
      facets: ['*'],
      // facetFilters: `enabled: true`, // only get enabled products
      filters,
      attributesToRetrieve: [], // no need to retrieve attributes for this search
    },
  },
}) // first complete search to get all available filters
if (initialResults.value?.facets) {
  initialFilters.value = mapFacets(
    initialResults.value.facets,
  ) as ZnFilterProps['filters']

  // Remove initial filter values with no products from selectedFilters
  if (selectedFilters.value) {
    Object.keys(selectedFilters.value).forEach((filterKey) => {
      const facet = initialFilters.value?.[filterKey as Filters]
      if (facet?.facet?.values) {
        selectedFilters.value[filterKey as Filters] = selectedFilters.value[
          filterKey as Filters
        ]?.filter((selectedValue) => {
          if (
            [
              KIDS_GENDER_GROUP_STRING,
              MEN_GENDER_GROUP_STRING,
              WOMEN_GENDER_GROUP_STRING,
              SPORTS_GROUP_STRING,
              WRAP_AROUND_GROUP.filter,
              CAT_EYE_GROUP.filter,
              FULL_RIM_GROUP.filter,
              HALF_RIM_GROUP.filter,
              'prescription',
            ].includes(selectedValue)
          ) {
            return true
          }
          return facet.facet.values.some(
            (value: any) =>
              value.value === selectedValue && value.productCount > 0,
          )
        })

        // Remove the filter key if no values remain
        if (selectedFilters.value[filterKey as Filters]?.length === 0) {
          delete selectedFilters.value[filterKey as Filters]
        }
      }
    })
  }

  updateInitialFilterFacets()
}
</script>
